import {
    Calendar,
    CircleAlert,
    CircleCheck,
    ClipboardCheck,
    Info,
    TriangleAlert,
    Trophy
} from 'lucide-react';
import React, { useState } from 'react';
import { DatePicker } from '@components/date-picker';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { Separator } from '@components/ui/separator';
import { toast } from '@core/toast';
import { cn } from '@core/utils/cn';
import {
    BadgeTypeMultiSelect,
    BadgeTypeOption
} from './badge-type-multi-select';
import { injectTSDI } from '../../../core/tsdi';
import { Api } from '../../../core/api';
import {
    BadgeCategoryAdminDto,
    BadgeKind,
    BadgeTypeAdminDto
} from '@api/FitnessActivityClient';

interface AssignGoalModalProps {
    open: boolean;
    onClose: () => void;
    userId: string;
    onSuccess: () => void;
}

interface AssignmentResult {
    userId: string;
    badgeTypeId: number;
    badgeTypeName: Record<string, string>;
    goalId: number | null;
    goalAssignmentId: number | null;
    success: boolean;
    alreadyAssigned: boolean;
    errorMessage?: string;
}

interface AssignmentResponse {
    totalRequested: number;
    successCount: number;
    alreadyAssignedCount: number;
    failedCount: number;
    noGoalCount: number;
    results: AssignmentResult[];
}

const getLocalizedName = (name: Record<string, string> | undefined): string => {
    if (!name) return 'Unnamed';
    return name['en'] || name[Object.keys(name)[0]] || 'Unnamed';
};

const ResultIcon = ({ result }: { result: AssignmentResult }) => {
    if (result.success) {
        return <CircleCheck className="size-4 shrink-0 text-success" />;
    }
    if (result.alreadyAssigned) {
        return <TriangleAlert className="size-4 shrink-0 text-warning" />;
    }
    if (!result.goalId) {
        return <Info className="size-4 shrink-0 text-muted-foreground" />;
    }
    return <CircleAlert className="size-4 shrink-0 text-destructive" />;
};

const resultText = (result: AssignmentResult) => {
    if (result.success) {
        return `Goal assigned (ID: ${result.goalAssignmentId})`;
    }
    if (result.alreadyAssigned) {
        return 'Goal already assigned to user';
    }
    if (!result.goalId) {
        return 'No linked goal';
    }
    return result.errorMessage || 'Failed to assign';
};

export const AssignGoalModal: React.FC<AssignGoalModalProps> = ({
    open,
    onClose,
    userId,
    onSuccess
}) => {
    const [selectedBadgeTypes, setSelectedBadgeTypes] = useState<
        BadgeTypeOption[]
    >([]);
    const [dayFrom, setDayFrom] = useState<Date | null>(new Date());
    const [dayTo, setDayTo] = useState<Date | null>(null);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<AssignmentResult[] | null>(null);
    const { fitnessActivityClient } = injectTSDI(Api);

    const badgeTypesWithoutGoal = selectedBadgeTypes.filter(
        (bt) => !bt.hasGoal
    );

    const formatDate = (date: Date | null): string | undefined => {
        if (!date) return undefined;
        return date.toISOString().split('T')[0];
    };

    const handleAssign = async () => {
        if (selectedBadgeTypes.length === 0) return;

        const assignableBadgeTypes = selectedBadgeTypes.filter(
            (bt) => bt.hasGoal
        );

        if (assignableBadgeTypes.length === 0) {
            toast.warning('No badge types with linked goals selected');
            return;
        }

        setLoading(true);
        try {
            const response =
                await fitnessActivityClient.admin.userBadgeAssignmentAdminControllerAssignGoalsFromBadgeTypes(
                    {
                        userIds: [userId],
                        badgeTypeIds: assignableBadgeTypes.map((bt) => bt.id),
                        dayFrom: formatDate(dayFrom),
                        dayTo: formatDate(dayTo)
                    }
                );

            const data = response.data as AssignmentResponse;
            setResults(data.results);

            if (data.successCount > 0) {
                toast.success(
                    `Successfully assigned ${data.successCount} goal(s). User can now earn badges by completing them.`
                );
                onSuccess();
            }
        } catch (error: any) {
            console.error('Failed to assign goals:', error);
            toast.error(
                error?.response?.data?.message || 'Failed to assign goals'
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchAllBadgeCategories = async (): Promise<
        BadgeCategoryAdminDto[]
    > => {
        const categories: BadgeCategoryAdminDto[] = [];
        let page = 0;
        let totalPages = 1;

        while (page < totalPages) {
            const response =
                await fitnessActivityClient.admin.badgeCategoryAdminControllerGetCategories(
                    {
                        page,
                        size: 200,
                        sort: ['sortOrder,ASC']
                    }
                );
            categories.push(...(response.data.content || []));
            totalPages = response.data.totalPages || 0;
            page += 1;
        }

        return categories;
    };

    const fetchAllBadgeTypesForCategory = async (
        categoryId: number
    ): Promise<BadgeTypeAdminDto[]> => {
        const types: BadgeTypeAdminDto[] = [];
        let page = 0;
        let totalPages = 1;

        while (page < totalPages) {
            const response =
                await fitnessActivityClient.admin.badgeTypeAdminControllerGetTypes(
                    {
                        categoryId,
                        page,
                        size: 200,
                        sort: ['sortOrder,ASC']
                    }
                );
            types.push(...(response.data.content || []));
            totalPages = response.data.totalPages || 0;
            page += 1;
        }

        return types;
    };

    const handleAssignStandardBadgeGoals = async () => {
        if (
            !window.confirm(
                'Assign all default STANDARD badge goals to this user?'
            )
        )
            return;

        setLoading(true);
        try {
            await fitnessActivityClient.admin.badgeSeedSupportControllerTriggerBadgeSeed();

            const categories = await fetchAllBadgeCategories();
            const standardCategoryIds = categories
                .filter(
                    (category) =>
                        category.badgeKind === BadgeKind.STANDARD &&
                        category.enabled &&
                        !category.deleted
                )
                .map((category) => category.id);

            if (standardCategoryIds.length === 0) {
                toast.warning(
                    'No STANDARD badge categories found after seeding'
                );
                return;
            }

            const standardBadgeTypes: BadgeTypeAdminDto[] = [];
            for (const categoryId of standardCategoryIds) {
                const types = await fetchAllBadgeTypesForCategory(categoryId);
                standardBadgeTypes.push(...types);
            }

            const badgeTypeIds = standardBadgeTypes
                .filter((type) => !type.deleted && !!type.goalId)
                .map((type) => type.id);

            if (badgeTypeIds.length === 0) {
                toast.warning(
                    'No STANDARD badge types with linked goals found'
                );
                return;
            }

            const response =
                await fitnessActivityClient.admin.userBadgeAssignmentAdminControllerAssignGoalsFromBadgeTypes(
                    {
                        userIds: [userId],
                        badgeTypeIds,
                        dayFrom: formatDate(dayFrom),
                        dayTo: formatDate(dayTo)
                    }
                );

            const data = response.data as AssignmentResponse;
            setResults(data.results);

            if (data.successCount > 0) {
                toast.success(
                    `Successfully assigned ${data.successCount} goal(s) from STANDARD badges.`
                );
                onSuccess();
            }
        } catch (error: any) {
            console.error('Failed to assign STANDARD badge goals:', error);
            toast.error(
                error?.response?.data?.message ||
                    'Failed to assign STANDARD badge goals'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedBadgeTypes([]);
        setDayFrom(new Date());
        setDayTo(null);
        setResults(null);
        onClose();
    };

    const assignableCount = selectedBadgeTypes.filter(
        (bt) => bt.hasGoal
    ).length;
    const successCount = results?.filter((r) => r.success).length || 0;
    const alreadyAssignedCount =
        results?.filter((r) => r.alreadyAssigned).length || 0;
    const failedCount =
        results?.filter((r) => !r.success && !r.alreadyAssigned && r.goalId)
            .length || 0;
    const noGoalCount = results?.filter((r) => !r.goalId).length || 0;

    return (
        <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                        <span className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
                            <ClipboardCheck className="size-5 text-primary" />
                        </span>
                        {results ? 'Assignment Results' : 'Assign Badge Goal'}
                    </DialogTitle>
                    <DialogDescription>
                        {results
                            ? `${successCount} of ${results.length} goals assigned`
                            : 'Select badge types to assign goals'}
                    </DialogDescription>
                </DialogHeader>

                {!results ? (
                    <div className="flex flex-col gap-4">
                        <Alert variant="info">
                            <Trophy />
                            <AlertDescription>
                                <span className="font-medium text-foreground">
                                    How Badge Goals Work
                                </span>
                                <span>
                                    When you assign a badge goal, the user can
                                    earn the badge by completing the linked
                                    goal. Progress is tracked automatically.
                                </span>
                            </AlertDescription>
                        </Alert>

                        <div className="flex items-center justify-between gap-4 rounded-lg border border-dashed border-success/40 bg-success/5 p-4">
                            <div>
                                <p className="text-sm font-medium">
                                    Quick action
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    Assign all STANDARD badge goals from the
                                    default seed (creates missing).
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleAssignStandardBadgeGoals}
                                disabled={loading}
                            >
                                <ClipboardCheck />
                                Assign STANDARD
                            </Button>
                        </div>

                        <BadgeTypeMultiSelect
                            value={selectedBadgeTypes}
                            onChange={setSelectedBadgeTypes}
                            label="Badge Types"
                        />

                        {badgeTypesWithoutGoal.length > 0 && (
                            <Alert variant="warning">
                                <TriangleAlert />
                                <AlertDescription>
                                    <span>
                                        {badgeTypesWithoutGoal.length} badge
                                        type(s) have no linked goal and will be
                                        skipped:
                                    </span>
                                    <span className="mt-1 flex flex-wrap gap-1">
                                        {badgeTypesWithoutGoal.map((bt) => (
                                            <Badge
                                                key={bt.id}
                                                variant="warning"
                                            >
                                                {getLocalizedName(bt.name)}
                                            </Badge>
                                        ))}
                                    </span>
                                </AlertDescription>
                            </Alert>
                        )}

                        <Separator />

                        <div className="flex items-center gap-2 text-sm font-medium">
                            <Calendar className="size-4 text-muted-foreground" />
                            Goal Period
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                            <DatePicker
                                label="Start Date"
                                value={dayFrom}
                                onChange={setDayFrom}
                            />
                            <div className="flex flex-col gap-1">
                                <DatePicker
                                    label="End Date (optional)"
                                    value={dayTo}
                                    onChange={setDayTo}
                                    clearable
                                />
                                <span className="text-xs text-muted-foreground">
                                    Leave empty for indefinite
                                </span>
                            </div>
                        </div>

                        {assignableCount > 0 && (
                            <div className="flex items-center justify-between rounded-lg border border-success/40 bg-success/5 px-4 py-3">
                                <span className="text-sm">Ready to assign</span>
                                <Badge variant="success">
                                    {assignableCount} goal
                                    {assignableCount > 1 ? 's' : ''}
                                </Badge>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-wrap gap-2">
                            {successCount > 0 && (
                                <Badge variant="success">
                                    <CircleCheck />
                                    {successCount} Assigned
                                </Badge>
                            )}
                            {alreadyAssignedCount > 0 && (
                                <Badge variant="warning">
                                    <TriangleAlert />
                                    {alreadyAssignedCount} Already had
                                </Badge>
                            )}
                            {failedCount > 0 && (
                                <Badge variant="destructive">
                                    <CircleAlert />
                                    {failedCount} Failed
                                </Badge>
                            )}
                            {noGoalCount > 0 && (
                                <Badge variant="outline">
                                    <Info />
                                    {noGoalCount} No goal
                                </Badge>
                            )}
                        </div>

                        <ul className="flex flex-col gap-1">
                            {results.map((result, idx) => (
                                <li
                                    key={idx}
                                    className={cn(
                                        'flex items-center gap-3 rounded-md px-3 py-2',
                                        result.success && 'bg-success/5',
                                        result.alreadyAssigned && 'bg-warning/5'
                                    )}
                                >
                                    <ResultIcon result={result} />
                                    <div>
                                        <p className="text-sm font-medium">
                                            {getLocalizedName(
                                                result.badgeTypeName
                                            )}
                                        </p>
                                        <span className="text-xs text-muted-foreground">
                                            {resultText(result)}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <DialogFooter>
                    <Button
                        variant={results ? 'default' : 'ghost'}
                        onClick={handleClose}
                    >
                        {results ? 'Done' : 'Cancel'}
                    </Button>
                    {!results && (
                        <Button
                            onClick={handleAssign}
                            disabled={assignableCount === 0}
                            loading={loading}
                        >
                            <ClipboardCheck />
                            Assign {assignableCount > 0
                                ? assignableCount
                                : ''}{' '}
                            Goal{assignableCount !== 1 ? 's' : ''}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
